import { useState } from "react";
import { fetchChatHistory, sendChat, clearChatHistory, fetchChatQuota } from "@/lib/api";
import { getWeatherSnapshot } from "@/lib/weatherClient";

// Chat session state + API wiring for the Mochi chat widget:
// history loading, optimistic user message, send with weather context,
// error bubble fallback, clearing the conversation, AND the daily chat
// quota (10 msgs / UTC day per user — cost & abuse control).
export function useChatSession({ onActions }) {
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [quota, setQuota] = useState(null); // { limit, used, remaining, exhausted, resets_at }

    const refreshQuota = () => {
        fetchChatQuota()
            .then(setQuota)
            .catch((e) => console.warn("chat quota fetch failed", e));
    };

    const loadHistory = () => {
        // Always refresh quota whenever the panel opens (lightweight).
        refreshQuota();
        if (loaded) return;
        fetchChatHistory().then((h) => {
            setMessages(h);
            setLoaded(true);
        }).catch((e) => {
            console.warn("chat history load failed", e);
            setLoaded(true);
        });
    };

    const send = async () => {
        const text = draft.trim();
        if (!text || sending) return;
        // Client-side guard — server is still the source of truth.
        if (quota?.exhausted) return;
        const tempId = `tmp-${Date.now()}`;
        setMessages((m) => [...m, { id: tempId, role: "user", text, created_at: new Date().toISOString() }]);
        setDraft("");
        setSending(true);
        try {
            // Inject weather/season snapshot (if the user opted in) so Mochi
            // can warmly reference what they're actually experiencing.
            const snap = getWeatherSnapshot();
            const client_context = snap ? { weather: snap.condition, season: snap.season } : null;
            const reply = await sendChat(text, "default", client_context);
            setMessages((m) => [...m, reply]);
            if (reply.actions && reply.actions.length > 0) {
                onActions?.(reply.actions);
            }
        } catch (e) {
            console.warn("chat send failed", e);
            setMessages((m) => [...m, {
                id: `err-${Date.now()}`,
                role: "assistant",
                text: "Hmm, I got distracted — can you say that again? 🌿",
                created_at: new Date().toISOString(),
            }]);
        }
        setSending(false);
        // Refresh quota AFTER send so the counter ticks down in real time
        // (the backend bumps the counter on every accepted turn — and
        // returns a sleepy block message once at the cap).
        refreshQuota();
    };

    const clear = async () => {
        await clearChatHistory();
        setMessages([]);
    };

    return { messages, draft, setDraft, sending, loaded, quota, loadHistory, send, clear };
}
